import { useState, useEffect } from "react";
import { apiPatch, apiPost } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UserCog, Lock, ShieldCheck, MonitorCog, Mail, KeyRound } from "lucide-react";

export function ProfileSettings({ open, onOpenChange }) {
  const { user, density, setDensity, reduceMotion, setReduceMotion } = useApp();
  const [form, setForm] = useState({ name: "", title: "", phone: "", mfa_enabled: false });
  const [pw, setPw] = useState({ a: "", b: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (user) setForm({ name: user.name || "", title: user.title || "", phone: user.phone || "", mfa_enabled: !!user.mfa_enabled }); }, [user, open]);
  if (!user) return null;

  const save = async () => {
    setSaving(true);
    try { await apiPatch(`/profile/${user.id}`, form); toast.success("Profile updated"); }
    catch { toast.error("Could not save profile"); }
    setSaving(false);
  };
  const resetPw = async () => {
    if (pw.a.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw.a !== pw.b) return toast.error("Passwords do not match");
    try { await apiPost(`/profile/${user.id}/password`, { new_password: pw.a }); toast.success("Password updated"); setPw({ a: "", b: "" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="profile-settings">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><UserCog className="h-4 w-4 text-navy" />Profile &amp; preferences</DialogTitle>
          <DialogDescription>Manage your identity, security and desktop experience.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="profile">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
            <TabsTrigger value="prefs" data-testid="tab-prefs">Desktop</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-3 pt-3">
            <div className="rounded-lg bg-muted/50 border p-3 flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0"><p className="text-[11px] text-muted-foreground flex items-center gap-1">Company email <Lock className="h-3 w-3" /> (managed)</p><p className="font-medium truncate" data-testid="profile-email">{user.email}</p></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Display name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Job title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="profile-title" /></div>
              <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Role (managed by admin)</Label><Input value={user.role} disabled data-testid="profile-role" /></div>
            <Button onClick={save} disabled={saving} data-testid="profile-save">{saving ? "Saving…" : "Save profile"}</Button>
          </TabsContent>

          <TabsContent value="security" className="space-y-4 pt-3">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-navy" /><div><p className="text-sm font-medium">Multi-factor authentication</p><p className="text-[11px] text-muted-foreground">Extra verification at sign-in.</p></div></div>
              <Switch checked={form.mfa_enabled} onCheckedChange={(v) => { setForm({ ...form, mfa_enabled: v }); apiPatch(`/profile/${user.id}`, { mfa_enabled: v }).then(() => toast.success(v ? "MFA enabled" : "MFA disabled")); }} data-testid="profile-mfa" />
            </div>
            <div className="rounded-lg border p-3 space-y-2">
              <p className="text-sm font-medium flex items-center gap-1.5"><KeyRound className="h-4 w-4 text-navy" />Reset password</p>
              <Input type="password" placeholder="New password (min 8 chars)" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} data-testid="pw-new" />
              <Input type="password" placeholder="Confirm new password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} data-testid="pw-confirm" />
              <Button size="sm" variant="outline" onClick={resetPw} data-testid="pw-reset">Update password</Button>
            </div>
          </TabsContent>

          <TabsContent value="prefs" className="space-y-4 pt-3">
            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5"><MonitorCog className="h-3.5 w-3.5" />Personalise your desktop. Themes, fonts, layout &amp; wallpaper live in the Appearance panel (palette icon).</p>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><p className="text-sm font-medium">Compact density</p><p className="text-[11px] text-muted-foreground">Tighter spacing across apps.</p></div>
              <Switch checked={density === "compact"} onCheckedChange={(v) => setDensity(v ? "compact" : "comfortable")} data-testid="pref-density" />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><p className="text-sm font-medium">Reduce motion</p><p className="text-[11px] text-muted-foreground">Minimise animations.</p></div>
              <Switch checked={!!reduceMotion} onCheckedChange={setReduceMotion} data-testid="pref-motion" />
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
